/**
 * Investor-portal logging helpers.
 *
 * The transport now lives in the app-wide logger (`@/utils/logger`); this module
 * keeps `portalLog` as a portal-flavoured alias plus the portal-specific
 * correlation helpers used by the login flow. Events emitted through `portalLog`
 * are auto-tagged `app: 'portal'` by the shared logger.
 */

import { log } from '@/utils/logger';

export { errorMessage } from '@/utils/logger';

/**
 * Structured logger for the investor portal. Alias of the shared app-wide
 * logger; every method no-ops when BetterStack is not configured.
 */
export const portalLog = log;

/** Short random id correlating the events of a single login attempt. */
export function newTraceId(): string {
  return crypto.randomUUID();
}

/**
 * Deterministic SHA-256 (hex, truncated) of a login email, for correlating
 * login events without shipping plaintext PII to the log store. Deterministic
 * so the same address always hashes alike (count attempts per identity, confirm
 * "was it X?" by hashing X's email). Not anonymization: email is low-entropy,
 * so this is reversible-if-guessed pseudonymity - HMAC would add nothing since
 * any key in a browser bundle is public.
 */
export async function hashEmail(email: string): Promise<string> {
  const normalized = email.trim().toLowerCase();
  const bytes = new TextEncoder().encode(normalized);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}
